/**
 * ImpersonationBanner (#1137 Task 3.4).
 *
 * Iron Oxide / Razor Citrine top-of-page chrome that's only rendered
 * when an impersonation grant is active in `ImpersonationContext`.
 * Shows the target identity, scope/workspace, a live countdown to
 * `expires_at`, and a STOP button that delegates to
 * `stopImpersonation()` on the context (which handles backend revoke +
 * navigation back to the actor's origin).
 *
 * Identity rendering rules:
 *   - scope === 'platform' → first 8 chars of target_user_id (avoid
 *     leaking real-user emails to over-the-shoulder readers when a
 *     platform admin acts cross-workspace).
 *   - scope === 'workspace' → target_email (the actor already has admin
 *     access to that workspace, so no info leak).
 *
 * Countdown re-renders every 30s via a tick state — cheap compared to
 * a 1s clock and good enough for an "Xh Ym" display. The interval is
 * cleared on unmount.
 *
 * Layout: `fixed top-0 left-0 right-0 h-10 z-[70]`. The Header
 * (`z-50`, `top-0` baseline) shifts to `top-10` when this banner is
 * mounted (see Header.js conditional offset) and a 40px pad pushes the
 * AppShell content so existing pt-14 page wrappers still clear chrome.
 *
 * z-index rationale (#1137 final-review N2): bumped from z-[60] to z-[70]
 * to sit above AssetPanel's fullscreen viewer (z-[60]) so the Stop button
 * stays reachable while a fullscreen asset is open. The confirm modal
 * (z-[80]) and PasswordGate (z-[100]) still render above the banner —
 * banner is always-on-top chrome but never above active modals.
 */
import React, { useEffect, useState } from 'react';
import { useImpersonation } from '../contexts/ImpersonationContext';

function fmtCountdown(expiresAt) {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return 'expired';
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function ImpersonationBanner() {
  const ctx = useImpersonation();
  const impersonation = ctx?.impersonation || null;
  const stopImpersonation = ctx?.stopImpersonation;

  // Tick state forces a re-render every 30s so the countdown freshens
  // without needing each consumer to re-mount the banner.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!impersonation) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, [impersonation]);

  if (!impersonation) return null;

  const targetLabel = impersonation.scope === 'platform'
    ? (impersonation.target_user_id || '').slice(0, 8)
    : (impersonation.target_email || '');

  const wsClause = impersonation.workspace_name
    ? ` in ${impersonation.workspace_name}`
    : '';

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[70] h-10 flex items-center justify-between px-4 uppercase text-sm tracking-wide"
      style={{
        background: '#A61E17',
        color: '#F2A900',
        fontFamily: 'IBM Plex Mono, monospace',
      }}
      data-testid="impersonation-banner"
    >
      <span>
        Impersonating {targetLabel} · acting as {impersonation.target_role}
        {wsClause} · expires in {fmtCountdown(impersonation.expires_at)}
      </span>
      <button
        type="button"
        onClick={() => stopImpersonation && stopImpersonation()}
        className="border border-current px-3 py-1 text-sm uppercase font-bold hover:bg-[#F2A900] hover:text-[#A61E17]"
        data-testid="impersonation-stop"
      >
        Stop
      </button>
    </div>
  );
}
